import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CheckCircle2, Loader2, MailX, XCircle } from 'lucide-react'

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
})

type Status =
  | 'loading'
  | 'ready'
  | 'already'
  | 'invalid'
  | 'submitting'
  | 'success'
  | 'error'

function UnsubscribePage() {
  const { token } = Route.useSearch()
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState<string>('')

  useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) {
          setStatus('invalid')
          return
        }
        if (data.valid === true) setStatus('ready')
        else if (data.reason === 'already_unsubscribed') setStatus('already')
        else setStatus('invalid')
      })
      .catch(() => setStatus('invalid'))
  }, [token])

  const confirm = async () => {
    setStatus('submitting')
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErrorMsg(data?.error || 'No se pudo procesar la baja')
        setStatus('error')
        return
      }
      if (data.success) setStatus('success')
      else if (data.reason === 'already_unsubscribed') setStatus('already')
      else setStatus('error')
    } catch (e: any) {
      setErrorMsg(e?.message || 'Error de red')
      setStatus('error')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            {status === 'success' || status === 'already' ? (
              <CheckCircle2 className="h-12 w-12 text-success" />
            ) : status === 'invalid' || status === 'error' ? (
              <XCircle className="h-12 w-12 text-destructive" />
            ) : status === 'loading' || status === 'submitting' ? (
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
            ) : (
              <MailX className="h-12 w-12 text-primary" />
            )}
          </div>
          <CardTitle>
            {status === 'success' && 'Te diste de baja'}
            {status === 'already' && 'Ya estás dado de baja'}
            {status === 'ready' && 'Confirmar baja'}
            {status === 'loading' && 'Verificando…'}
            {status === 'submitting' && 'Procesando…'}
            {status === 'invalid' && 'Enlace inválido'}
            {status === 'error' && 'Algo salió mal'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'ready' && (
            <>
              <p className="text-sm text-muted-foreground">
                Si confirmás, dejaremos de enviarte emails a esta dirección.
                Seguirás recibiendo notificaciones críticas relacionadas con tu
                cuenta y compras.
              </p>
              <Button onClick={confirm} className="w-full">
                Confirmar baja
              </Button>
            </>
          )}
          {status === 'success' && (
            <p className="text-sm text-muted-foreground">
              Listo. Tu dirección fue removida de nuestros envíos.
            </p>
          )}
          {status === 'already' && (
            <p className="text-sm text-muted-foreground">
              Esta dirección ya estaba dada de baja.
            </p>
          )}
          {status === 'invalid' && (
            <p className="text-sm text-muted-foreground">
              El enlace expiró o es inválido. Si querés darte de baja, usá el
              enlace del email más reciente que recibiste.
            </p>
          )}
          {status === 'error' && (
            <p className="text-sm text-destructive">{errorMsg}</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
